import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import { useEffect, useState } from "react";
import { AiOutlineEnter } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setOpenSearchBar, setSearchTerm } from "../redux/slices/authSlice";
import { highlightSearchTerm as highlight } from "../utils/highlightSearchTerm";

function levenshtein(str1, str2) {
  const len1 = str1.length;
  const len2 = str2.length;

  let matrix = Array(len1 + 1);
  for (let i = 0; i <= len1; i++) {
    matrix[i] = Array(len2 + 1);
  }

  for (let i = 0; i <= len1; i++) {
    matrix[i][0] = i;
  }

  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + 1
        );
      }
    }
  }

  return matrix[len1][len2];
}

function fuzzyScore(text, term) {
  if (!text) return 0;

  const t = text.toLowerCase();
  const s = term.toLowerCase();

  if (t.includes(s)) return 1;

  const distance = levenshtein(t, s);
  const similarity = 1 - distance / Math.max(t.length, s.length);

  return similarity > 0.6 ? similarity : 0;
}

const GlobalSearch = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    searchTerm,
    appData,
    openSearchBar: open,
  } = useSelector((state) => state.search);
  const [options, setOptions] = useState([]);
  const [index, setIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const placeholders = [
    "Search Emissions",
    "Search Reports",
    "Search Targets",
    "Search Activities",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % placeholders.length);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const iconStyles = {
    style: {
      background: "#e8e8e8",
      color: "#333",
      padding: "2px 6px",
      borderRadius: 4,
      fontSize: 12,
      border: "1px solid #d0d0d0",
      minWidth: 28,
      textAlign: "center",
    },
  };

  useEffect(() => {
    if (!searchTerm) {
      setOptions([]);
      setSelectedIndex(0);
      return;
    }

    const results = [];

    Object.entries(appData ?? {}).forEach(([moduleName, moduleValue]) => {
      Object.entries(moduleValue ?? {}).forEach(
        ([sectionName, sectionValue]) => {
          if (typeof sectionValue !== "object") return;

          Object.entries(sectionValue).forEach(([subSectionName, subArray]) => {
            if (!Array.isArray(subArray)) return;

            subArray.forEach((group) => {
              group.forEach((item) => {
                const tabMatches = (item.ui?.tab || []).some((tab) =>
                  fuzzyScore(tab, searchTerm),
                );

                const score = Math.max(
                  fuzzyScore(moduleName, searchTerm),
                  fuzzyScore(sectionName, searchTerm),
                  fuzzyScore(subSectionName, searchTerm),
                  fuzzyScore(item.description, searchTerm),
                  tabMatches ? 0.8 : 0,
                );

                if (score > 0) {
                  results.push({
                    route: item.route,
                    score,

                    label: (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            color: "#8c8c8c",
                            textTransform: "uppercase",
                          }}
                        >
                          <span>{moduleName}</span>
                          {" • "}
                          <span>{sectionName}</span>
                          {" • "}
                          <span>{subSectionName}</span>
                        </div>

                        <div style={{ fontSize: 14, fontWeight: 500 }}>
                          <span>{item.description}</span>
                        </div>
                      </div>
                    ),
                  });
                }
              });
            });
          });
        },
      );
    });

    results.sort((a, b) => b.score - a.score);

    setOptions(results);
    setSelectedIndex(0);
  }, [searchTerm, appData]);

  const handleSearch = (value) => {
    dispatch(setSearchTerm(value));
  };

  const handleSelect = (option) => {
    navigate(option.route);
    dispatch(setOpenSearchBar(false));
    dispatch(setSearchTerm(""));
  };

  const handleClose = () => {
    dispatch(setOpenSearchBar(false));
    dispatch(setSearchTerm(""));
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;

      if (e.key === "Escape") {
        handleClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < options.length - 1 ? prev + 1 : prev,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === "Enter" && options.length > 0) {
        e.preventDefault();
        handleSelect(options[selectedIndex]);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, options, selectedIndex]);

  // Auto scroll to selected item
  useEffect(() => {
    const selectedElement = document.getElementById(
      `search-result-${selectedIndex}`,
    );
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedIndex]);

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      closable={false}
      mask={true}
      width={700}
      styles={{
        body: { padding: 0, margin: 0 },
        mask: {
          background: "rgba(0, 0, 0, 0.25)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        },
      }}
      style={{ top: 60 }}
      destroyOnClose
      getContainer={false}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 8,
          overflow: "hidden",
          border: "1px solid #e0e0e0",
        }}
      >
        {/* Search Input */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "16px 20px",
            borderBottom: "1px solid #e8e8e8",
          }}
        >
          <SearchOutlined
            style={{
              fontSize: 20,
              color: "#00984A",
              marginRight: 12,
            }}
          />
          <input
            autoFocus
            type="text"
            placeholder={placeholders[index]}
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              fontSize: 16,
              color: "#333",
              background: "transparent",
            }}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <CloseOutlined
              onClick={handleClose}
              style={{
                fontSize: 16,
                color: "#999",
                cursor: "pointer",
              }}
            />
          </div>
        </div>

        {/* Search Results */}
        <div
          style={{
            maxHeight: 400,
            overflowY: "auto",
            border: "1px solid #e0e0e0",
          }}
        >
          {options.length > 0 ? (
            options.map((option, index) => {
              const isSelected = index === selectedIndex;
              const isHovered = index === hoveredIndex;
              const isActive = isSelected || isHovered;

              return (
                <div
                  key={index}
                  id={`search-result-${index}`}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => {
                    setHoveredIndex(index);
                    setSelectedIndex(index);
                  }}
                  onMouseLeave={() => setHoveredIndex(null)}
                  style={{
                    padding: "12px 16px",
                    borderBottom:
                      index < options.length - 1 ? "1px solid gray" : "none",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    background: isActive ? "#e2f8e0" : "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: 14,
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      color: isActive ? "#1a1a1a" : "#333",
                      fontWeight: isActive ? 500 : 400,
                    }}
                  >
                    {option.label}
                  </div>
                  {isActive && (
                    <AiOutlineEnter
                      style={{
                        fontSize: 20,
                        color: "#10b981",
                        fontWeight: "bolder",
                        marginLeft: 12,
                        flexShrink: 0,
                      }}
                    />
                  )}
                </div>
              );
            })
          ) : searchTerm ? (
            <div
              style={{
                padding: "40px 20px",
                textAlign: "center",
                color: "#9ca3af",
                fontSize: 14,
              }}
            >
              No results found
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "4px 20px",
            background: "#f5f5f5",
            borderTop: "1px solid #e8e8e8",
          }}
        >
          <div style={{ display: "flex", gap: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ display: "flex", gap: 4 }}>
                <kbd {...iconStyles}>↓</kbd>
                <kbd {...iconStyles}>↑</kbd>
              </div>
              <span style={{ color: "#666", fontSize: 13 }}>Navigate</span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <kbd {...iconStyles}>↵</kbd>
              <span style={{ color: "#666", fontSize: 13 }}>Select</span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <kbd
                style={{
                  background: "#e8e8e8",
                  color: "#333",
                  padding: "2px 8px",
                  borderRadius: 4,
                  fontSize: 11,
                  border: "1px solid #d0d0d0",
                }}
              >
                ESC
              </kbd>
              <span style={{ color: "#666", fontSize: 13 }}>Close</span>
            </div>
          </div>
        </div>
      </div>
      <style>
        {`.search-placeholder-wrapper {
                    position: absolute;
                    pointer-events: none;
                    left: 12px;
                    top: 50%;
                    text-align: center;
                    font-size: 12px;
                    transform: translateY(-40%);
                  }

              .search-placeholder {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                text-align: center;
                gap: 6px;
                animation: fadeSlide 0.8s ease alternate;
              }

              @keyframes fadeSlide {
                from {
                  opacity: 0;
                  transform: translateY(6px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }`}
      </style>
    </Modal>
  );
};

export default GlobalSearch;
